import { Breakpoints, BreakpointWrapper, CTA, Divider, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { ReactElement, useEffect } from 'react';
import {
  Calendar,
  CarSettings,
  Contact,
  ContactDealer,
  Mail,
  Phone,
  Pin,
  Profile,
  Service,
  TechnicalSpecification,
} from '@volkswagen-onehub/icons-core';
import styled from 'styled-components';
import { Detail } from './Detail';
import { StepDetails } from './StepDetails';
import { VerifyBookingDetailsPageViewProps } from './VerifyBookingDetailsPageViewProps';
import { Steps } from '../../modules/wizard/Steps';
import { buildDealerAddress } from '../../utils/DealerAddressBuilder';
import { formatDate } from '../../utils/DateUtils';
import { useTracking } from '../../tracking';
import { PageRoutes } from "../../modules/navigation/PageRoutes";
import { Titles } from "../../modules/wizard/Tiltes";
import { maskEmail, maskPhoneNumber } from "../../utils/MaskUtils";

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;
const StyledGroup = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${Breakpoints.b960}px) {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: start;

    // StepDetails
    & > :nth-child(1) {
      grid-column: 1;
    }

    // Divider container
    & > :nth-child(2) {
      grid-column: 2;
      padding: 0 ${(props) => props.theme.size.static350};
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    // StepDetails
    & > :nth-child(3) {
      grid-column: 3;
    }
  }
`;
const StyledMobileDivider = styled.div`
  margin: ${(props) => props.theme.size.static350} 0;
`;
const StyledCTA = styled(CTA)`
  margin-top: ${(props) => props.theme.size.dynamic0150};
`;

const StyledPage = styled.div`
  .title {
    margin-bottom: ${(props) => props.theme.size.dynamic0150};
  }
`;

const StyledError = styled.div`
  color: red !important;
  margin-top: 8px;

  * {
    color: red !important;
  }
`;

function MobileDivider(): ReactElement {
  return (
    <BreakpointWrapper max={Breakpoints.b960}>
      <StyledMobileDivider>
        <Divider />
      </StyledMobileDivider>
    </BreakpointWrapper>
  );
}

export function VerifyBookingDetailsPageVwView({
   bookingState,
   pageContext,
   onVerifyClick,
   recaptchaError,
}: VerifyBookingDetailsPageViewProps): ReactElement {
  const trackingService = useTracking();

  useEffect(() => {
    trackingService.trackFormStepLoad({
      form: { FormName: Titles[Steps.ContactDetails] },
    });
  }, []);


  return (
    <StyledPage>
      <Text className="title" appearance={TokenTextAppearance.headline400}>
        <Text bold>Review and submit</Text>
      </Text>
      <StyledLayout>
        <StyledGroup>
          <StepDetails step={Steps.SelectService} bookingState={bookingState} stepDescription={PageRoutes.ServiceRequest}>
            <Detail text="Your mileage" value={pageContext.serviceRequest.mileage} icon={<CarSettings variant="default" ariaHidden />} />
            <Detail text="Your service" value={pageContext.serviceRequest.serviceType.name} icon={<Service />} />
            <Detail
              text="Additional notes"
              value={pageContext.serviceRequest.serviceComments ?? 'Not provided'}
              icon={<TechnicalSpecification />}
            />
          </StepDetails>
          <div>
            <MobileDivider />
          </div>
          <StepDetails
            step={Steps.SelectDealer}
            bookingState={bookingState}
            stepDescription={PageRoutes.SelectDealer}
            customTitle={
              <Text appearance={TokenTextAppearance.headline300}>
                Selected <Text bold>dealer</Text>
              </Text>
            }
          >
            <Detail text="Name" value={pageContext.selectedDealer.name} icon={<ContactDealer />} />
            <Detail text="Address" value={buildDealerAddress(pageContext.selectedDealer)} icon={<Pin />} />
          </StepDetails>
        </StyledGroup>
        <MobileDivider />
        <StyledGroup>
          <StepDetails step={Steps.ContactDetails} bookingState={bookingState}
                       stepDescription={PageRoutes.CustomerContact}>
            <Detail
              text="Name"
              value={`${pageContext.customerContact.title} ${pageContext.customerContact.name} ${pageContext.customerContact.surname}`}
              icon={<Profile />}
            />
            <Detail text="Email" value={maskEmail(pageContext.customerContact.email)} icon={<Mail />} />
            <Detail text="Phone number" value={maskPhoneNumber(pageContext.customerContact.phoneNumber)}
                    icon={<Phone />} />
            <Detail text="Preferred method of contact" value={pageContext.customerContact.preferredContactMethodType}
                    icon={<Contact />} />
          </StepDetails>
          <div>
            <MobileDivider />
          </div>
          <div>
            <StepDetails
              step={Steps.Appointment}
              bookingState={bookingState}
              stepDescription={PageRoutes.SelectServiceDate}
              customTitle={
                <Text appearance={TokenTextAppearance.headline300}>
                  Your <Text bold>appointment</Text>
                </Text>
              }
            >
              <Detail text="Date" value={formatDate(pageContext.selectedSlot.date, "short")} icon={<Calendar />} />
            </StepDetails>
          </div>
        </StyledGroup>
        <MobileDivider />
      </StyledLayout>
      <Text>
        By confirming this information, you acknowledge that you have read,
        <br />
        understood, and agreed to the terms and conditions governing this
        <br />
        service.
        <br />
        <br />
        You also consent to the processing of your personal information in
        <br />
        accordance with the Protection of Personal Information Act (POPIA)
      </Text>

      <StyledCTA
        tag="button"
        onClick={() => {
          trackingService.trackFormCTAClick({
            form: { FormName: Titles[Steps.ContactDetails] },
          });
          onVerifyClick();
        }}
        stretchContent={{
          [Breakpoints.default]: true,
          [Breakpoints.b560]: false,
        }}
      >
        Submit
      </StyledCTA>

      {recaptchaError && (
        <StyledError>
          <p>{recaptchaError}</p>
        </StyledError>
      )}
    </StyledPage>
  );
}
